import React, { useRef, useEffect, useState } from 'react';
import { X, ChevronDown, Info } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import MerkmaleFilter from './MerkmaleFilter';
import MultiSelectDropdown from './MultiSelectDropdown';

/**
 * Erweiterter Filterbereich fuer das Lager-Modul (Phase 3b + 3c).
 * Aufgerufen aus MaterialFilters.js wenn 'Mehr Filter' aktiv ist.
 *
 * Props:
 *   filterOptions        { lagerorte: [], beschaffungsarten: [], dispomerkmale: [] }
 *   advFilters           { lagerort: [], beschaffungsart: [], dispomerkmal: [],
 *                          bestand_min: '', bestand_max: '',
 *                          offene_banf: 'alle', offene_bestellung: 'alle',
 *                          handlungsbedarf: false }
 *   onChange             (patch: Partial<advFilters>) => void
 *   onReset              () => void   — setzt alle Filter (Schnell + erweitert) zurueck
 *   hasAnyActive         boolean      — ob irgendein Filter (Schnell oder erw.) aktiv ist
 *   // Phase 3c — Merkmals-Filter
 *   user                 Object       — Auth-User (fuer Klassifizierungs-API)
 *   werk                 string       — Aktuell gewaehltes Werk
 *   selectedWg           string[]     — Aktuell gewaehlte Warengruppen
 *   merkmale             Array        — Aktive Merkmals-Filter [{name, values?|min?,max?}]
 *   onMerkmaleChange     (merkmale: Array) => void
 */
const AdvancedFilters = ({
  filterOptions, advFilters, onChange, onReset, hasAnyActive,
  user, werk, selectedWg, merkmale, onMerkmaleChange,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [lgOpen, setLgOpen] = useState(false);
  const [baOpen, setBaOpen] = useState(false);
  const [dmOpen, setDmOpen] = useState(false);
  const lgRef = useRef(null);
  const baRef = useRef(null);
  const dmRef = useRef(null);

  useEffect(() => {
    const handle = (e) => {
      if (lgRef.current && !lgRef.current.contains(e.target)) setLgOpen(false);
      if (baRef.current && !baRef.current.contains(e.target)) setBaOpen(false);
      if (dmRef.current && !dmRef.current.contains(e.target)) setDmOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const safeLagerorte = Array.isArray(filterOptions?.lagerorte) ? filterOptions.lagerorte : [];
  const safeBeschaffungsarten = Array.isArray(filterOptions?.beschaffungsarten) ? filterOptions.beschaffungsarten : [];
  const safeDispomerkmale = Array.isArray(filterOptions?.dispomerkmale) ? filterOptions.dispomerkmale : [];
  const safeEinkaeufergruppen = Array.isArray(filterOptions?.einkaeufergruppen) ? filterOptions.einkaeufergruppen : [];
  const safeLieferanten = Array.isArray(filterOptions?.sonderbestand_lieferanten) ? filterOptions.sonderbestand_lieferanten : [];

  const selLagerort = Array.isArray(advFilters.lagerort) ? advFilters.lagerort : [];
  const selBeschaffungsart = Array.isArray(advFilters.beschaffungsart) ? advFilters.beschaffungsart : [];
  const selDispomerkmal = Array.isArray(advFilters.dispomerkmal) ? advFilters.dispomerkmal : [];
  const selStockType = Array.isArray(advFilters.stock_type) ? advFilters.stock_type : [];
  const selVendor = Array.isArray(advFilters.vendor) ? advFilters.vendor : [];
  const selEkgrp = Array.isArray(advFilters.ekgrp) ? advFilters.ekgrp : [];

  const STOCK_TYPE_OPTS = [
    { value: 'own', label: t('lm.stockTypeOwn') },
    { value: 'vendor_consignment', label: t('lm.stockTypeKonsi') },
    { value: 'at_vendor', label: t('lm.stockTypeAtVendor') },
  ];

  const BESCHAFFUNGSART_LABELS = {
    F: t('lm.beschaffungsartF'),
    E: t('lm.beschaffungsartE'),
    X: t('lm.beschaffungsartX'),
  };

  const toggle = (arr, value) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const chipBase =
    'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 cursor-pointer select-none';
  const chipActive = isDarkMode
    ? 'bg-brand/25 border-brand/50 text-blue-200'
    : 'bg-brand/10 border-brand/40 text-brand';
  const chipInactive = isDarkMode
    ? 'bg-white/8 border-white/15 text-white/60 hover:bg-white/12'
    : 'bg-white border-slate-200 text-slate-600 hover:border-brand/30 hover:text-brand';

  const dropdownCls = `absolute z-40 mt-1 w-56 rounded-xl shadow-lg border overflow-hidden ${
    isDarkMode
      ? 'bg-gray-900 border-white/15 shadow-[0_8px_32px_rgba(0,0,0,0.5)]'
      : 'bg-white border-slate-200 shadow-lg'
  }`;

  const inputCls = `text-xs rounded-lg border outline-none px-2 py-1.5 w-28 ${
    isDarkMode
      ? 'bg-white/8 border-white/15 text-white placeholder-white/30 focus:border-brand/50'
      : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400 focus:border-brand/50'
  }`;

  const toggleChipRow = (field, value) => (
    <button
      key={value}
      type="button"
      onClick={() => onChange({ [field]: value })}
      className={`${chipBase} ${advFilters[field] === value ? chipActive : chipInactive}`}
    >
      {value === 'alle'
        ? t('lm.filterAlle')
        : value === 'mit'
        ? t('lm.filterMit')
        : t('lm.filterOhne')}
    </button>
  );

  // Aktive erweiterte Filter als entfernbare Chips
  const activeChips = [];

  selLagerort.forEach((lg) => {
    const item = safeLagerorte.find((l) => l.lgort === lg);
    activeChips.push(
      <span key={`lg-${lg}`} className={`${chipBase} ${chipActive}`}>
        {item?.lgort_name ? `${lg} – ${item.lgort_name}` : lg}
        <button
          type="button"
          onClick={() => onChange({ lagerort: toggle(selLagerort, lg) })}
          className="ml-0.5 hover:text-red-400 transition-colors"
          aria-label={t('common.remove')}
        >
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  });

  selBeschaffungsart.forEach((ba) => {
    activeChips.push(
      <span key={`ba-${ba}`} className={`${chipBase} ${chipActive}`}>
        {BESCHAFFUNGSART_LABELS[ba] || ba}
        <button
          type="button"
          onClick={() => onChange({ beschaffungsart: toggle(selBeschaffungsart, ba) })}
          className="ml-0.5 hover:text-red-400 transition-colors"
          aria-label={t('common.remove')}
        >
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  });

  selDispomerkmal.forEach((dm) => {
    activeChips.push(
      <span key={`dm-${dm}`} className={`${chipBase} ${chipActive}`}>
        {dm}
        <button
          type="button"
          onClick={() => onChange({ dispomerkmal: toggle(selDispomerkmal, dm) })}
          className="ml-0.5 hover:text-red-400 transition-colors"
          aria-label={t('common.remove')}
        >
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  });

  selStockType.forEach((st) => {
    const lbl = STOCK_TYPE_OPTS.find((o) => o.value === st)?.label || st;
    activeChips.push(
      <span key={`st-${st}`} className={`${chipBase} ${chipActive}`}>
        {lbl}
        <button type="button" onClick={() => onChange({ stock_type: toggle(selStockType, st) })} className="ml-0.5 hover:text-red-400 transition-colors" aria-label={t('common.remove')}>
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  });

  selVendor.forEach((v) => {
    activeChips.push(
      <span key={`vd-${v}`} className={`${chipBase} ${chipActive}`}>
        {t('lm.filterLieferant')}: {v}
        <button type="button" onClick={() => onChange({ vendor: toggle(selVendor, v) })} className="ml-0.5 hover:text-red-400 transition-colors" aria-label={t('common.remove')}>
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  });

  selEkgrp.forEach((eg) => {
    activeChips.push(
      <span key={`eg-${eg}`} className={`${chipBase} ${chipActive}`}>
        {t('lm.filterEinkaeufergruppe')}: {eg}
        <button type="button" onClick={() => onChange({ ekgrp: toggle(selEkgrp, eg) })} className="ml-0.5 hover:text-red-400 transition-colors" aria-label={t('common.remove')}>
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  });

  if (advFilters.sperrbestand) {
    activeChips.push(
      <span key="sperrbestand" className={`${chipBase} ${chipActive}`}>
        {t('lm.filterSperrbestand')}
        <button type="button" onClick={() => onChange({ sperrbestand: false })} className="ml-0.5 hover:text-red-400 transition-colors" aria-label={t('common.remove')}>
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  }

  if (advFilters.qmbestand) {
    activeChips.push(
      <span key="qmbestand" className={`${chipBase} ${chipActive}`}>
        {t('lm.filterQmbestand')}
        <button type="button" onClick={() => onChange({ qmbestand: false })} className="ml-0.5 hover:text-red-400 transition-colors" aria-label={t('common.remove')}>
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  }

  if (advFilters.bestand_min !== '' && advFilters.bestand_min != null) {
    activeChips.push(
      <span key="bestand_min" className={`${chipBase} ${chipActive}`}>
        {t('lm.filterBestandVon')}: {advFilters.bestand_min}
        <button
          type="button"
          onClick={() => onChange({ bestand_min: '' })}
          className="ml-0.5 hover:text-red-400 transition-colors"
          aria-label={t('common.remove')}
        >
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  }

  if (advFilters.bestand_max !== '' && advFilters.bestand_max != null) {
    activeChips.push(
      <span key="bestand_max" className={`${chipBase} ${chipActive}`}>
        {t('lm.filterBestandBis')}: {advFilters.bestand_max}
        <button
          type="button"
          onClick={() => onChange({ bestand_max: '' })}
          className="ml-0.5 hover:text-red-400 transition-colors"
          aria-label={t('common.remove')}
        >
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  }

  if (advFilters.handlungsbedarf) {
    activeChips.push(
      <span key="handlungsbedarf" className={`${chipBase} ${chipActive}`}>
        {t('lm.handlungsbedarf')}
        <button
          type="button"
          onClick={() => onChange({ handlungsbedarf: false })}
          className="ml-0.5 hover:text-red-400 transition-colors"
          aria-label={t('common.remove')}
        >
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  }

  return (
    <div className={`px-4 py-3 border-b ${isDarkMode ? 'bg-gray-900/20 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
      {/* Zeile 1: Filter-Controls */}
      <div className="flex flex-wrap items-center gap-2">

        {/* Lagerort Dropdown */}
        <div className="relative" ref={lgRef}>
          <button
            type="button"
            onClick={() => { setLgOpen((v) => !v); setBaOpen(false); setDmOpen(false); }}
            className={`${chipBase} ${selLagerort.length > 0 ? chipActive : chipInactive} gap-1.5`}
          >
            {t('lm.filterLagerort')}
            {selLagerort.length > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isDarkMode ? 'bg-brand/40 text-blue-100' : 'bg-brand text-white'}`}>
                {selLagerort.length}
              </span>
            )}
            <ChevronDown className={`w-3 h-3 transition-transform ${lgOpen ? 'rotate-180' : ''}`} />
          </button>
          {lgOpen && (
            <div className={dropdownCls}>
              <div className="max-h-48 overflow-y-auto py-1">
                {safeLagerorte.length === 0 ? (
                  <p className={`px-3 py-2 text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('lm.empty')}</p>
                ) : (
                  safeLagerorte.map((lg) => (
                    <button
                      key={lg.lgort}
                      type="button"
                      onClick={() => onChange({ lagerort: toggle(selLagerort, lg.lgort) })}
                      className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors ${
                        isDarkMode ? 'hover:bg-white/8 text-white/80' : 'hover:bg-slate-50 text-slate-700'
                      } ${selLagerort.includes(lg.lgort) ? (isDarkMode ? 'bg-brand/15' : 'bg-brand/5') : ''}`}
                    >
                      <span className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${
                        selLagerort.includes(lg.lgort) ? 'bg-brand border-brand' : isDarkMode ? 'border-white/30' : 'border-slate-300'
                      }`}>
                        {selLagerort.includes(lg.lgort) && <X className="w-2.5 h-2.5 text-white" />}
                      </span>
                      <span className={`font-mono text-[10px] flex-shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{lg.lgort}</span>
                      <span className="truncate">{lg.lgort_name || lg.lgort}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Beschaffungsart Dropdown */}
        <div className="relative" ref={baRef}>
          <button
            type="button"
            onClick={() => { setBaOpen((v) => !v); setLgOpen(false); setDmOpen(false); }}
            className={`${chipBase} ${selBeschaffungsart.length > 0 ? chipActive : chipInactive} gap-1.5`}
          >
            {t('lm.filterBeschaffungsart')}
            {selBeschaffungsart.length > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isDarkMode ? 'bg-brand/40 text-blue-100' : 'bg-brand text-white'}`}>
                {selBeschaffungsart.length}
              </span>
            )}
            <ChevronDown className={`w-3 h-3 transition-transform ${baOpen ? 'rotate-180' : ''}`} />
          </button>
          {baOpen && (
            <div className={dropdownCls}>
              <div className="py-1">
                {/* Immer F/E/X anzeigen; wenn Backend beschaffungsarten liefert, auf diese einschraenken */}
                {(safeBeschaffungsarten.length > 0 ? safeBeschaffungsarten : ['F', 'E', 'X']).map((ba) => (
                  <button
                    key={ba}
                    type="button"
                    onClick={() => onChange({ beschaffungsart: toggle(selBeschaffungsart, ba) })}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors ${
                      isDarkMode ? 'hover:bg-white/8 text-white/80' : 'hover:bg-slate-50 text-slate-700'
                    } ${selBeschaffungsart.includes(ba) ? (isDarkMode ? 'bg-brand/15' : 'bg-brand/5') : ''}`}
                  >
                    <span className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${
                      selBeschaffungsart.includes(ba) ? 'bg-brand border-brand' : isDarkMode ? 'border-white/30' : 'border-slate-300'
                    }`}>
                      {selBeschaffungsart.includes(ba) && <X className="w-2.5 h-2.5 text-white" />}
                    </span>
                    <span className={`font-mono text-[10px] flex-shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{ba}</span>
                    <span>{BESCHAFFUNGSART_LABELS[ba] || ba}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Dispositionsmerkmal Dropdown */}
        <div className="relative" ref={dmRef}>
          <button
            type="button"
            onClick={() => { setDmOpen((v) => !v); setLgOpen(false); setBaOpen(false); }}
            className={`${chipBase} ${selDispomerkmal.length > 0 ? chipActive : chipInactive} gap-1.5`}
          >
            {t('lm.filterDispomerkmal')}
            {selDispomerkmal.length > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isDarkMode ? 'bg-brand/40 text-blue-100' : 'bg-brand text-white'}`}>
                {selDispomerkmal.length}
              </span>
            )}
            <ChevronDown className={`w-3 h-3 transition-transform ${dmOpen ? 'rotate-180' : ''}`} />
          </button>
          {dmOpen && (
            <div className={dropdownCls}>
              <div className="max-h-48 overflow-y-auto py-1">
                {safeDispomerkmale.length === 0 ? (
                  <p className={`px-3 py-2 text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('lm.empty')}</p>
                ) : (
                  safeDispomerkmale.map((dm) => (
                    <button
                      key={dm}
                      type="button"
                      onClick={() => onChange({ dispomerkmal: toggle(selDispomerkmal, dm) })}
                      className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors ${
                        isDarkMode ? 'hover:bg-white/8 text-white/80' : 'hover:bg-slate-50 text-slate-700'
                      } ${selDispomerkmal.includes(dm) ? (isDarkMode ? 'bg-brand/15' : 'bg-brand/5') : ''}`}
                    >
                      <span className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${
                        selDispomerkmal.includes(dm) ? 'bg-brand border-brand' : isDarkMode ? 'border-white/30' : 'border-slate-300'
                      }`}>
                        {selDispomerkmal.includes(dm) && <X className="w-2.5 h-2.5 text-white" />}
                      </span>
                      <span>{dm}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Bestand von/bis */}
        <div className="flex items-center gap-1.5">
          <span className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{t('lm.filterBestandVon')}</span>
          <input
            type="number"
            min="0"
            value={advFilters.bestand_min ?? ''}
            onChange={(e) => onChange({ bestand_min: e.target.value })}
            placeholder="0"
            className={inputCls}
          />
          <span className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{t('lm.filterBestandBis')}</span>
          <input
            type="number"
            min="0"
            value={advFilters.bestand_max ?? ''}
            onChange={(e) => onChange({ bestand_max: e.target.value })}
            placeholder="∞"
            className={inputCls}
          />
        </div>

        {/* Offene BANF Toggle-Chips */}
        <div className="flex items-center gap-1.5">
          <span className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{t('lm.filterOffeneBanf')}:</span>
          {['alle', 'mit', 'ohne'].map((v) => toggleChipRow('offene_banf', v))}
        </div>

        {/* Offene Bestellung Toggle-Chips */}
        <div className="flex items-center gap-1.5">
          <span className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{t('lm.filterOffeneBestellung')}:</span>
          {['alle', 'mit', 'ohne'].map((v) => toggleChipRow('offene_bestellung', v))}
        </div>

        {/* Handlungsbedarf Chip */}
        <div className="relative group flex items-center gap-1">
          <button
            type="button"
            onClick={() => onChange({ handlungsbedarf: !advFilters.handlungsbedarf })}
            className={`${chipBase} ${advFilters.handlungsbedarf ? chipActive : chipInactive}`}
          >
            {t('lm.handlungsbedarf')}
          </button>
          <div className="relative">
            <Info className={`w-3.5 h-3.5 cursor-help ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`} />
            <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 rounded-lg text-xs opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 ${
              isDarkMode ? 'bg-gray-800 text-white/80 border border-white/10' : 'bg-slate-800 text-white'
            }`}>
              {t('lm.handlungsbedarfHint')}
            </div>
          </div>
        </div>

        {/* Sonderbestandsart (Eigen / Konsignation / beim Lieferanten) */}
        <MultiSelectDropdown
          label={t('lm.filterSonderbestand')}
          options={STOCK_TYPE_OPTS}
          selected={selStockType}
          onChange={(next) => onChange({ stock_type: next })}
        />

        {/* Lieferant (Sonderbestand-Eigentümer) — nur sinnvoll wenn Sonderbestände existieren */}
        {safeLieferanten.length > 0 && (
          <MultiSelectDropdown
            label={t('lm.filterLieferant')}
            options={safeLieferanten}
            selected={selVendor}
            onChange={(next) => onChange({ vendor: next })}
            searchable
          />
        )}

        {/* Einkäufergruppe */}
        <MultiSelectDropdown
          label={t('lm.filterEinkaeufergruppe')}
          options={safeEinkaeufergruppen}
          selected={selEkgrp}
          onChange={(next) => onChange({ ekgrp: next })}
          searchable
        />

        {/* Bestandsqualität (Eigenbestand-Status, KEIN Sonderbestand): Sperr-/QM-Bestand */}
        <div className="flex items-center gap-1.5">
          <span className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{t('lm.filterBestandsqualitaet')}:</span>
          <button
            type="button"
            onClick={() => onChange({ sperrbestand: !advFilters.sperrbestand })}
            className={`${chipBase} ${advFilters.sperrbestand ? chipActive : chipInactive}`}
          >
            {t('lm.filterSperrbestand')}
          </button>
          <button
            type="button"
            onClick={() => onChange({ qmbestand: !advFilters.qmbestand })}
            className={`${chipBase} ${advFilters.qmbestand ? chipActive : chipInactive}`}
          >
            {t('lm.filterQmbestand')}
          </button>
        </div>

        {/* Alle Filter zuruecksetzen */}
        {hasAnyActive && (
          <button
            type="button"
            onClick={onReset}
            className={`ml-auto text-xs font-medium transition-colors ${
              isDarkMode ? 'text-white/40 hover:text-white/70' : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            {t('lm.advancedFilterReset')}
          </button>
        )}
      </div>

      {/* Zeile 2: Aktive erweiterte Filter-Chips */}
      {activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {activeChips}
        </div>
      )}

      {/* Zeile 3: Dynamische Klassifizierungs-Merkmale (Phase 3c) */}
      <MerkmaleFilter
        user={user}
        werk={werk}
        selectedWg={Array.isArray(selectedWg) ? selectedWg : []}
        merkmale={Array.isArray(merkmale) ? merkmale : []}
        onMerkmaleChange={onMerkmaleChange}
      />
    </div>
  );
};

export default AdvancedFilters;
